import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { useLocation, UNSAFE_NavigationContext } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { hasTrackingConsent } from "../utils/userConsent";

/**
 * Safely get location - returns null if not inside Router context
 */
function useSafeLocation() {
  // Check if we're inside a Router by looking for NavigationContext
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const hasRouter = navigationContext != null;

  // Only call useLocation if we're inside a Router
  // This is safe because the condition is stable (context doesn't change mid-render)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = hasRouter ? useLocation() : null;
  return location;
}

/**
 * App-wide PostHog capture hook. Use this for all analytics events—do not call
 * usePostHog().capture() directly so Admin users are always excluded.
 *
 * Events are suppressed when:
 * - The user is in the "Admin" group
 * - The user has not consented to tracking (userConsent.userTracking is false)
 *
 * For auth flows where the user isn't in Redux yet (e.g. right after login),
 * pass options.isAdmin from the auth response.
 *
 * Every event is automatically enriched with:
 * - path: current route pathname (when inside a Router)
 *
 * @example
 * const { capture } = usePostHogCapture();
 * capture(POSTHOG_EVENTS.ASSET_GROUP_SELECTED, { asset_group_id: id, asset_group_name: name });
 *
 * @example Auth flow (user not in Redux yet)
 * capture(POSTHOG_EVENTS.LOGIN_SUCCESS, { method: "email" }, { isAdmin: details?.groups?.includes("Admin") ?? false });
 *
 * @example Compliance events that bypass tracking consent
 * capture(POSTHOG_EVENTS.TERMS_ACCEPTED, { terms_key }, { bypassConsent: true });
 */
export function usePostHogCapture() {
  const posthog = usePostHog();
  const location = useSafeLocation();
  const isAdminFromRedux = useSelector(
    (state: { user?: { details?: { groups?: string[] } } }) =>
      state.user?.details?.groups?.includes("Admin") ?? false
  );
  const userConsentRaw = useSelector(
    (state: { user?: { permissionDetails?: { userConsent?: string } } }) =>
      state.user?.permissionDetails?.userConsent ?? null
  );

  const capture = useCallback(
    (
      eventName: string,
      properties?: Record<string, unknown>,
      options?: { isAdmin?: boolean; bypassConsent?: boolean }
    ) => {
      const isAdmin = options?.isAdmin ?? isAdminFromRedux;
      const consentOk = options?.bypassConsent || hasTrackingConsent(userConsentRaw);
      if (posthog && !isAdmin && consentOk) {
        const enriched = {
          ...(location?.pathname && { path: location.pathname }),
          ...properties,
        };
        posthog.capture(eventName, enriched);
      }
    },
    [posthog, isAdminFromRedux, userConsentRaw, location?.pathname]
  );

  return { capture };
}
